import axios from "axios";
import cn from "classnames";
import { Contract, JsonRpcProvider, isAddress } from "ethers";
import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import nodeABI from "../../abis/NODEABI.json";
import NodeTable from "../../components/NodeTable.jsx";
import ReferralHistoryTable from "../../components/ReferralHistoryTable.jsx";
import ClaimXBRtable from "../../components/claimXBRtable.js";
import ellipse from "./../../assets/ellipse-header-node.png";
import {getEthersSigner} from '../../utils/wagmi.utils.js'

import {
  claimXBRRewardsForNodes,
  getClaimXBRDetails,
  getCurrentBlockNumber,
  getMiningRewards,
  getNodeIds,
  getTotalSupply,
  totalClaimedXBRRewards,
} from "../../uniswap/utils.js";
import BuyNodeModal from "../newBuyNode/BuyNodeModal.jsx";
import TransferNodeModal from "../transferNode/TransferNodeModal.jsx";
import Analytics from "./components/Analytics.jsx";
import FilterMenuMobile from "./components/FilterMenuMbile.jsx";
import FilterSection from "./components/FilterSection.jsx";
import GlobalFounders from "./components/GlobalFounders.jsx";
import MyNodes from "./components/MyNodes.jsx";
import NodeHeroScreen from "./components/NodeHeroScreen.jsx";
import { Loader } from "../../components/Loader/index.jsx";
import { useContextProvider } from '../../context/index.jsx';
import { toast } from 'react-toastify';

const NodeComponent = () => {
	const {walletAddress, jsonRpcProvider}= useContextProvider();
  const [activeTab, setActiveTab] = useState("Transaction");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimTransactions, setClaimTransactions] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [nodesLeft, setNodesLeft] = useState(5000);
  const [claimedRewards, setClaimedRewards] = useState(0);
  const [earnedRewards, setEarnedRewards] = useState(0);
  const [nodes, setNodes] = useState([]);
  const [referralHistory, setReferralHistory] = useState([]);
  const [rewardsDistributed, setRewardsDistributed] = useState(0);
  const [unclaimedXBR, setUnclaimedXBR] = useState(0);
  const [claimXBRTotal, setClaimXBRTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenBuyNodeModal, setIsOpenBuyNodeModal] = useState(false);
  const [isOpenTransferNodeModal, setIsOpenTransferNodeModal] = useState(false);

	const resetState = () => {
		setTransactions([]);
		setLoading(false);
		setError(null);
		setClaimTransactions([]);
		setSelectedCard(null);
		setClaimedRewards(0);
		setEarnedRewards(0);
		setNodes([]);
		setReferralHistory([]);
		setRewardsDistributed(0);
		setUnclaimedXBR(0);
		setClaimXBRTotal(0);
		setIsLoading(false);
		setIsOpenBuyNodeModal(false);
		setIsOpenTransferNodeModal(false);
	};

  const closeBuyNodeModal = () => {
    setIsOpenBuyNodeModal(false);
  };

  const openBuyNodeModal = () => {
    setIsOpenBuyNodeModal(true);
  };

  const closeTransferNodeModal = () => {
    setIsOpenTransferNodeModal(false);
  };

  const openTransferNodeModal = () => {
    setIsOpenTransferNodeModal(true);
  };

  const fetchTotalSupply = async () => {
    try {
      const totalSupply = await getTotalSupply(
        process.env.REACT_APP_CONTRACT_ADDRESS,
        jsonRpcProvider
      );
      setNodesLeft(5000 - Number(totalSupply));
    } catch (error) {
      setError("Error fetching total supply");
    }
  };

  const fetchRewards = async () => {
    try {
      const rewardsFromBlc = await getMiningRewards(jsonRpcProvider, walletAddress);
      const {
        totalRewards = 0,
        totalClaims = 0,
        ...dynamicObjects
      } = rewardsFromBlc || {};

      const dynamicRewardsArray = Object.values(dynamicObjects).filter(
        (item) => typeof item === "object"
      );

      const totalRewardsEarned = dynamicRewardsArray.reduce(
        (acc, reward) => Number(acc) + Number(reward.rewardsEarned),
        0
      );

      setClaimedRewards(totalClaims);
      setEarnedRewards(parseFloat(totalRewardsEarned).toFixed(6));
      setNodes(dynamicRewardsArray);
    } catch (error) {
      setError("Error fetching rewards");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllData = () => {
    Promise.all([
      fetchTotalSupply(),
      fetchRewards(),
      getTransactionEvents(),
      getClaimXBRDetails(),
    ]).then(() => setLoading(false));

    getReferralHistory();
    getNodeIds();
    totalRewardsDistributed();
    totalUnclaimedRewards();
  };

  const [isVerifying, setIsVerifying] = useState(false);

  const handleClaimXbrRewards = async ({ nodeId }) => {
    try {
			const signer = await getEthersSigner()
      setIsVerifying(true);
      await claimXBRRewardsForNodes(
        nodeId,
        signer,
        jsonRpcProvider,
      );

      setSelectedCard(null)

      toast.success('Claimed successfully!')
    } catch (error) {
      console.error("Error claiming rewards:", error);
      setError("Error claiming rewards");
    } finally {
      setLoading(true);
      setIsVerifying(false);
      fetchAllData();
    }
  };

  const getReferralHistory = async () => {
    try {
      const URL = process.env.REACT_APP_API;
      const response = await axios.get(
        `${URL}/v1/xbr/transactions/${walletAddress}`
      );
      setReferralHistory(response.data);
    } catch (error) {
      console.error("Error fetching referralHistory data:", error);
    }
  };

  const totalRewardsDistributed = async () => {
    const currentBlock = await getCurrentBlockNumber();
    const miningStartedBlockNumber = Number(
      process.env.REACT_APP_MINING_STARTED_BLOCK_NUMBER
    );
    const rewardPerBlock = Number(process.env.REACT_APP_REWARD_PER_BLOCK);
    const total =
      (Number(currentBlock) - miningStartedBlockNumber) * rewardPerBlock;
    setRewardsDistributed(total);
    return total;
  };

  const totalUnclaimedRewards = async () => {
    const claimedRewards = await totalClaimedXBRRewards();
    const totalDistributedRewards = await totalRewardsDistributed();
    const total = Number(totalDistributedRewards) - Number(claimedRewards);
    setUnclaimedXBR(total);
    setClaimXBRTotal(claimedRewards);
  };

  useEffect(() => {
    if (isAddress(walletAddress)) {
      setLoading(true);
      fetchAllData();
      setIsLoading(true);
    } else {
			resetState()
		}
  }, [walletAddress, jsonRpcProvider]);

  useEffect(() => {
    const fetchClaimData = async () => {
      try {
        const response = await getClaimXBRDetails(walletAddress);
        setClaimTransactions(response);
      } catch (error) {
        console.error("Error fetching claimXbrData data:", error);
      }
    };

    walletAddress && fetchClaimData(walletAddress);
  }, [walletAddress]);

  const getTransactionEvents = async () => {
    try {
      const contractABI = nodeABI;
      const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS;
      const apiKey = "Z4C4BRJD8EB51HEP4HWPX5745FB3P2TP5I";
      const baseUrl = "https://api.polygonscan.com/api";
      const URL = `${baseUrl}?module=account&action=txlist&address=${walletAddress}&startblock=0&endblock=99999999&sort=asc&apikey=${apiKey}`;

      const providerUrl = "https://polygon-rpc.com";
      const provider = new JsonRpcProvider(providerUrl);

      const transferEventSignature =
        "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";

      const contract = new Contract(contractAddress, contractABI, provider);

      const response = await axios.get(URL);
      const allTransactions = response.data.result;

      const filteredTransactions = allTransactions.filter(
        (tx) =>
          tx.to.toLowerCase() === contractAddress.toLowerCase() ||
          tx.from.toLowerCase() === contractAddress.toLowerCase()
      );

      const txHashs = filteredTransactions.map((tx) => tx.hash);

      const results = [];

      for (const txHash of txHashs) {
        const receipt = await provider.getTransactionReceipt(txHash);
        const transferLogs = receipt.logs.filter(
          (log) => log.topics[0] === transferEventSignature
        );

        const nodeIds = transferLogs
          .map((log) => {
            try {
              const parsedLog = contract.interface.parseLog(log);
              return parsedLog.args[2].toString();
            } catch (error) {
              if (error.code === "BUFFER_OVERRUN") {
                return null;
              } else {
                console.error("Error parsing log:", error);
                return null;
              }
            }
          })
          .filter((nodeId) => nodeId !== null);

        const transaction = filteredTransactions.find(
          (tx) => tx.hash === txHash
        );
        if (nodeIds.length > 0) {
          results.push({
            tx_hash: txHash,
            blockNumber: transaction.blockNumber,
            timeStamp: transaction.timeStamp,
            nodeIds: nodeIds.map(Number),
          });
        }
      }
      setTransactions(results);
    } catch (error) {
      setError("Error fetching transaction events");
      console.error(error);
    }
  };

  const handleTransferNode = () => {
    openTransferNodeModal();
  };

  const handleCardClick = ({ nodeId, reward }) => {
    setSelectedCard({ nodeId, reward });
  };

  const isCardSelected = (index) => {
    return selectedCard && selectedCard.nodeId === index;
  };

  const totalReferralAmount =
    referralHistory && referralHistory.length > 0
      ? referralHistory.reduce((acc, referral) => acc + referral.amount, 0)
      : 0;

  const setCurrentActiveTab = (value) => {
    setActiveTab(value);
  };
  const hasNodes = nodes.length > 0;

  return (
    <div className="font-inter pb-[20px] pt-[31px] lg:pb-[102px] relative">
      <NodeHeroScreen openBuyNodeModal={openBuyNodeModal} />
      <img
        src={ellipse}
        alt="background decoration"
        className="absolute -top-18 md:-top-30 md:h-fit right-0 left-0 mx-auto "
      />
      <div className="relative">
        {isLoading ? (
          <div className="relative flex items-center justify-center w-full h-full">
            <Loader />
          </div>
        ) : (
          <>
            <div className="w-full px-1 lg:px-8 z-50 mx-auto">
              <div className="grid lg:grid-cols-[366px_auto] gap-y-7 grid-flow-row gap-x-8 w-full">
                <div className="flex flex-col flex-col-reverse gap-7 sm:flex-row lg:flex-col">
                  <MyNodes
                    nodes={nodes}
                    handleCardClick={handleCardClick}
                    isCardSelected={isCardSelected}
                    transferNode={handleTransferNode}
                    openBuyNodeModal={openBuyNodeModal}
                  />
                  <GlobalFounders
                    hasNodes={hasNodes}
                    rewardsDistributed={rewardsDistributed}
                    unclaimedXBR={unclaimedXBR}
                    claimXBRTotal={claimXBRTotal}
                    nodesLeft={nodesLeft}
                  />
                </div>
                <div className={cn("z-50")}>
                  {hasNodes && (
                    <div>
                      <Analytics
                        claimedRewards={claimedRewards}
                        earnedRewards={earnedRewards}
                        totalReferralAmount={totalReferralAmount}
                        selectedCard={selectedCard}
                        handleClaimXbrRewards={handleClaimXbrRewards}
                        isVerifying={isVerifying}
                      />
                    </div>
                  )}

                  <div
                    className={cn(
                      "bg-white h-[700px]  inline-block dark:bg-darkCardBg px-2 pb-2 lg:px-4 lg:pb-4 w-full rounded-3xl shadow-smallShadow",

                      {
                        "h-[750px] xl:h-[850px] mt-0 xl:mt-[20px] custom1402:mt-0":
                          hasNodes,
                      }
                    )}
                  >
                    <FilterMenuMobile
                      isActiveTab={activeTab}
                      chooseActiveTab={setCurrentActiveTab}
                    />
                    <FilterSection
                      isActiveTab={activeTab}
                      chooseActiveTab={setCurrentActiveTab}
                    />

                    {activeTab === "Transaction" && (
                      <NodeTable
                        hasNodes={hasNodes}
                        activeTab={activeTab}
                        connectedWalletAddress={walletAddress}
                        transactions={transactions}
                      />
                    )}
                    {activeTab === "Referral" && (
                      <ReferralHistoryTable
                        hasNodes={hasNodes}
                        referrdata={referralHistory}
                      />
                    )}
                    {activeTab === "ClaimXBR" && (
                      <ClaimXBRtable
                        hasNodes={hasNodes}
                        claimData={claimTransactions}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Toaster />
            <BuyNodeModal
              openModal={isOpenBuyNodeModal}
              closeModal={closeBuyNodeModal}
            />
            <TransferNodeModal
              openModal={isOpenTransferNodeModal}
              closeModal={closeTransferNodeModal}
              nodes={nodes}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NodeComponent;
